"use client";

import type React from "react";
import Link from "next/link";
import { useClientValue } from "@/lib/hooks";
import { useDimFavicon } from "@/lib/favicon";
import { ROUTES, SITE } from "@/content/site";

let cachedRayId = "";

function makeRayId(): string {
  const hex = Array.from(crypto.getRandomValues(new Uint8Array(8)))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 16)}`;
}

const enter = (vars: Record<string, string>) => vars as React.CSSProperties;

export function NotFoundContent() {
  // Stable per page view; the server renders placeholders (no hydration mismatch).
  const rayId = useClientValue(() => (cachedRayId ||= makeRayId()), "········");
  // Grey tab icon: the requested page could not be shown
  useDimFavicon("not-found");

  return (
    <section className="cf404" aria-labelledby="cf404-title">
      <div className="cf404-frame">
        <div
          className="cf404-code cf404-in"
          aria-hidden
          style={enter({ "--in-y": "0px", "--in-blur": "10px", "--in-dur": "0.8s" })}
        >
          404
        </div>

        <h1
          id="cf404-title"
          className="cf404-title cf404-in"
          style={enter({ "--in-delay": "0.1s" })}
        >
          صفحه پیدا نشد
        </h1>

        <p className="cf404-lead cf404-in" style={enter({ "--in-y": "10px", "--in-delay": "0.16s" })}>
          آدرسی که درخواست کرده‌اید روی این سایت وجود ندارد، یا به نشانی دیگری منتقل
          شده است.
        </p>

        <div
          className="cf404-grid cf404-in"
          style={enter({ "--in-y": "16px", "--in-dur": "0.6s", "--in-delay": "0.24s" })}
        >
          <section className="cf404-card">
            <h2>چه اتفاقی افتاد؟</h2>
            <ul>
              <li>نشانی صفحه اشتباه تایپ شده است.</li>
              <li>صفحه حذف یا به نشانی جدیدی منتقل شده است.</li>
              <li>لینکی که دنبال کرده‌اید قدیمی یا ناقص است.</li>
            </ul>
          </section>
          <section className="cf404-card">
            <h2>چه کاری می‌توانید بکنید؟</h2>
            <ul>
              <li>نشانی را دوباره بررسی کنید.</li>
              <li>از صفحهٔ اصلی سایت ادامه دهید.</li>
              <li>محصولات یا وبلاگ را از دکمه‌های زیر باز کنید.</li>
            </ul>
          </section>
        </div>

        <nav
          className="cf404-actions cf404-in"
          aria-label="پیوندهای پیشنهادی"
          style={enter({ "--in-y": "0px", "--in-dur": "0.5s", "--in-delay": "0.32s" })}
        >
          <Link className="cf404-btn cf404-btn--primary" href={ROUTES.home}>
            بازگشت به خانه
          </Link>
          <Link className="cf404-btn" href={ROUTES.products}>
            محصولات
          </Link>
          <Link className="cf404-btn" href={ROUTES.weblog}>
            وبلاگ
          </Link>
          <Link className="cf404-btn" href={ROUTES.about}>
            درباره ما
          </Link>
        </nav>

        <footer className="cf404-foot">
          <p>
            <span className="cf404-foot-label">Ray ID</span>
            <code className="cf404-ray">{rayId}</code>
          </p>
          <p>
            نیاز به راهنمایی دارید؟{" "}
            <a href={`tel:${SITE.phone}`} data-track="call_click" data-track-label="404">
              {SITE.phoneDisplay}
            </a>
          </p>
        </footer>
      </div>
    </section>
  );
}
